<!-- eslint-disable max-len -->
<script>
import { chessboard } from "vue-chessboard";
import { Chessground } from "chessground";
import { EventBus } from "@/services/event-bus";
import { playAudio } from "@/utils/audio-helper";

export default {
  name: "BaseChessboard",

  extends: chessboard,

  props: {
    playerColor: {
      type: String,
      default: "",
    },
    pgn: {
      type: String,
      default: "",
    },
    matchResult: {
      type: String,
      default: null,
    },
    audio: {
      type: Boolean,
      default: false,
    },
    enablePremove: {
      type: Boolean,
      default: false,
    },
    autoQueen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      premove: null,
    };
  },

  watch: {
    playerColor: function () {
      this.loadPgn();
    },
    pgn: function () {
      this.loadPgn();
    },
    orientation: function () {
      this.loadPgn();
    },
    matchResult: function () {
      this.loadPgn();
    },
    showThreats: function () {
      this.loadPgn();
    },
    enablePremove: function () {
      this.loadPgn();
    },
  },

  methods: {
    playAudio() {
      if (this.audio) {
        if (this.game.game_over()) {
          playAudio("end");
          return;
        }
        const moves = this.game.history({ verbose: true });
        if (moves.length) {
          if (moves[moves.length - 1].captured) playAudio("capture");
          else playAudio("move");
        }
      }
    },

    viewOnly() {
      return this.matchResult || !this.playerColor;
    },

    loadPgn() {
      if (this.pgn) {
        // FIXME: temp fix by replacing the first full Move index with 1. so that game.js can load pgn
        this.game.load_pgn(this.pgn.replace(/\n\n\d+\.\s/, "\n1. "));
      }

      const config = {
        orientation: this.orientation ? this.orientation : "white",
        fen: this.game.fen(),
        turnColor: this.toColor(),
        viewOnly: this.viewOnly(),
        check: this.game.in_check(),
        highlight: {
          lastMove: true,
          check: true,
        },
        movable: {
          color: this.playerColor,
          free: this.free,
          dests: this.possibleMoves(),
          events: { after: this.userPlay() },
        },
        premovable: {
          enabled: this.enablePremove,
          showDests: true,
          castle: true,
          dests: this.possibleMoves(),
          events: {
            set: (orig, dest) => {
              this.premove = { orig: orig, dest: dest };
            },
            unset: () => {
              this.premove = null;
            },
          },
        },
      };

      if (this.playerColor && !this.matchResult) {
        config.drawable = { enabled: false };
      }

      // set last move to be highlighted
      const history = this.game.history({ verbose: true });
      if (history.length) {
        const lastMove = history[history.length - 1];
        config.lastMove = [lastMove.from, lastMove.to];
      }

      if (!this.board) this.board = Chessground(this.$refs.board, config);
      else this.board.set(config);
      this.afterMove();

      const result = this.gameResult();
      if (result) {
        console.log("loadPgn game_over", result);
        this.$emit("gameOver", result);
      }
    },

    gameResult() {
      if (!this.game.game_over()) {
        return null;
      }

      if (this.game.in_checkmate()) {
        return "checkmate";
      }

      if (this.game.in_stalemate()) {
        return "stalemate";
      }

      if (this.game.in_threefold_repetition()) {
        return "threefold_repetition";
      }

      if (this.game.insufficient_material()) {
        return "insufficient_material";
      }

      if (this.game.in_draw()) {
        return "draw";
      }

      return "game over";
    },

    userPlay() {
      return (orig, dest) => {
        if (this.isPromotion(orig, dest)) {
          console.log("isPromotion move", orig, dest);
          if (this.autoQueen) {
            this.makeMove(orig, dest, "q");
          } else {
            const promotingColor = this.toColor();
            EventBus.$emit("onPromotion", orig, dest, promotingColor);
          }
        } else {
          this.makeMove(orig, dest);
        }
      };
    },

    makeMove(from, to, promotion) {
      this.game.move({ from, to, promotion });

      // Send move data to gameModule to handle
      this.emitMoveData(from, to, !!this.premove);

      this.afterMove();
    },

    emitMoveData(orig, dest, isPremove = false) {
      const moveData = {
        threats: this.countThreats(this.toColor()) || {},
        history: this.game.history(),
        fen: this.game.fen(),
        pgn: this.game.pgn(),
        orig,
        dest,
        isPremove,
      };
      this.$emit("onMove", moveData);
    },

    afterMove() {
      this.calculatePromotions();
      // Don't show threats to players on their live game
      if (this.showThreats && !(this.playerColor && !this.matchResult)) {
        this.paintThreats();
      }

      this.playAudio();

      if (this.premove) {
        this.board.playPremove();
      }

      // Override this method to handle AI move
    },
  },

  mounted() {
    EventBus.$on("onPromotionSelected", this.makeMove);
  },

  beforeDestroy() {
    EventBus.$off("onPromotionSelected", this.makeMove);
  },
};
</script>

<style scoped>
/*
 * Chessground base css properties with customization.
 *
 * You need to include the css files in themes folder in order to have the
 * board and pieces displayed!
 */

.cg-board-wrap {
  width: 320px;
  height: 320px;
  position: relative;
  display: block;
  /* left: 15px;  */
  /* to display cord rank on the left side */
}
.cg-board {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  line-height: 0;
  background-size: cover;
  cursor: pointer;
}
.cg-board square {
  position: absolute;
  top: 0;
  left: 0;
  width: 12.5%;
  height: 12.5%;
}
.cg-board square.move-dest {
  background: radial-gradient(
    rgba(20, 85, 30, 0.5) 22%,
    #208530 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}
.cg-board square.premove-dest {
  background: radial-gradient(
    rgba(20, 30, 85, 0.5) 22%,
    #203085 0,
    rgba(0, 0, 0, 0.3) 0,
    rgba(0, 0, 0, 0) 0
  );
}
.cg-board square.oc.move-dest {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(20, 85, 0, 0.3) 80%
  );
}
.cg-board square.oc.premove-dest {
  background: radial-gradient(
    transparent 0%,
    transparent 80%,
    rgba(20, 30, 85, 0.2) 80%
  );
}
.cg-board .cg-square.move-dest.drag-over,
.cg-board .cg-square.premove-dest.drag-over {
  box-shadow: inset 0 0 10px 2px rgba(216, 85, 0, 0.9);
}
.cg-board square.last-move {
  will-change: transform;
  background-color: rgba(155, 199, 0, 0.41);
}
.cg-board square.selected {
  background-color: rgba(20, 85, 30, 0.5);
}
.cg-board square.check {
  background: radial-gradient(
    ellipse at center,
    rgba(255, 0, 0, 1) 0%,
    rgba(231, 0, 0, 1) 25%,
    rgba(169, 0, 0, 0) 89%,
    rgba(158, 0, 0, 0) 100%
  );
}
.cg-board square.current-premove {
  background-color: rgba(20, 30, 85, 0.5);
}
.cg-board piece {
  position: absolute;
  top: 0;
  left: 0;
  width: 12.5%;
  height: 12.5%;
  background-size: cover;
  z-index: 2;
  will-change: transform;
}
.cg-board piece.dragging {
  cursor: move;
  z-index: 9;
}
.cg-board piece.anim {
  z-index: 8;
}
.cg-board piece.fading {
  z-index: 1;
  opacity: 0.5;
}
.cg-board-wrap div.over {
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(20, 85, 30, 0.3);
}
.cg-board-wrap piece.ghost {
  opacity: 0.3;
}
.cg-board-wrap svg {
  overflow: hidden;
  position: relative;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  opacity: 0.6;
}
.cg-board-wrap svg image {
  opacity: 0.5;
}
.cg-board-wrap coords {
  position: absolute;
  display: flex;
  pointer-events: none;
  opacity: 0.8;
  font-size: 9px;
}
.cg-board-wrap coords.ranks {
  right: -15px;
  top: 0;
  flex-flow: column-reverse;
  height: 100%;
  width: 12px;
}
.cg-board-wrap coords.ranks.black {
  flex-flow: column;
}
.cg-board-wrap coords.files {
  bottom: -16px;
  left: 0;
  flex-flow: row;
  width: 100%;
  height: 16px;
  text-transform: uppercase;
  text-align: center;
}
.cg-board-wrap coords.files.black {
  flex-flow: row-reverse;
}
.cg-board-wrap coords coord {
  flex: 1 1 auto;
}
.cg-board-wrap coords.ranks coord {
  transform: translateY(39%);
}
</style>
